<template>
  <module-template
    id="scrolltainer"
    :title="$t('global.concepts.file-cleanup')"
  >
    <v-row dense class="mb-3">
      <v-col>
        <basic-card-horizontal
          v-if="diskSpaceItem"
          :headers="diskSpaceHeaders"
          :item="diskSpaceItem"
        >
          <template #[`item.availableDiskSpace`]="{ item }">
            {{ item.availableDiskSpace }}
            (
            <span :style="item.percentageStyle">
              {{ item.percentage + "%" }}
            </span>
            )
          </template>
        </basic-card-horizontal>
      </v-col>

      <v-col>
        <basic-card-horizontal :headers="unusedFvHeaders" :item="unusedFvItem">
        </basic-card-horizontal>
      </v-col>
    </v-row>

    <v-card class="mb-3">
      <v-card-title>
        {{ $t("global.file.unused-files") }}
      </v-card-title>

      <v-card-subtitle>
        {{ $t("global.file.unused-files-details") }}
      </v-card-subtitle>

      <v-skeleton-loader :loading="loadingUnused" type="table">
        <v-card-text>
          <v-expansion-panels focusable v-model="panel">
            <v-expansion-panel
              v-for="item in unusedPerEnv"
              :key="item.envLabel"
            >
              <v-expansion-panel-header>
                <template #default="{ open }">
                  <v-row no-gutters align="center">
                    <v-col cols="1">
                      <file-env-chip
                        :color="item.envColor"
                        :label="item.envLabel"
                        class="ml-1"
                      >
                      </file-env-chip>
                    </v-col>

                    <v-col cols="1">
                      <v-icon small> pic-contract </v-icon>
                      {{ item.count }}
                      {{ $t("global.menu.files").toLowerCase() }}
                    </v-col>

                    <v-col cols="1">
                      <v-icon> mdi-cloud-outline </v-icon>
                      {{ formatNumber(item.totalSizeMB, 0) }} MB
                    </v-col>

                    <v-spacer />

                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        small
                        v-if="$store.state.fileDeletePermission && open"
                        :disabled="!selectedUnusedVersions?.length"
                        class="ma-2"
                        @click.stop="onDeleteUnused(selectedUnusedVersions)"
                      >
                        {{ $t("global.action.delete-selected") }}
                        ({{ selectedUnusedVersions?.length ?? 0 }})
                      </v-btn>
                    </v-col>

                    <v-col cols="auto">
                      <v-btn
                        color="error"
                        small
                        v-if="$store.state.fileDeletePermission && open"
                        class="ma-2"
                        @click.stop="onDeleteUnused(item.fileVersions)"
                      >
                        {{ $t("global.action.delete-all") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </template>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <ooliba-basic-table
                  v-model="selectedUnusedVersions"
                  :headers="unusedVersionsTableHeaders"
                  :items="item.fileVersions"
                  :show-select="$store.state.fileDeletePermission"
                >
                  <template #[`item.name`]="{ item }">
                    <router-link
                      class="no-underline"
                      :to="`/file/view/${item.fileId}/${item.version + 1}`"
                    >
                      <span>{{ item.name }}</span>
                    </router-link>
                  </template>

                  <template #[`item.sizeMB`]="{ item }">
                    {{ formatNumber(item.sizeMB, 0) }} MB
                  </template>

                  <template #[`item.version`]="{ item }">
                    {{ item.version + 1 }}
                  </template>
                </ooliba-basic-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-skeleton-loader>
    </v-card>

    <v-card>
      <v-card-title> {{ $t("global.file.largest-files") }}</v-card-title>
      <v-card-subtitle>
        {{ $t("global.file.largest-files-details") }}
      </v-card-subtitle>

      <v-card-text>
        <ooliba-basic-table
          :headers="largestVersionsTableHeaders"
          :items="largestFileVersions"
          :loading="loadingLargest"
          no-pagination
        >
          <template #[`item.name`]="{ item }">
            <router-link
              class="no-underline"
              :to="`/file/view/${item.fileId}/${item.version + 1}`"
            >
              <span>{{ item.name }}</span>
            </router-link>
          </template>

          <template #[`item.sizeMB`]="{ item }">
            {{ formatNumber(item.sizeMB, 0) }} MB
          </template>

          <template #[`item.version`]="{ item }">
            {{ item.version + 1 }}
          </template>

          <template #[`item.runsUsingVersion`]="{ item }">
            <span
              @click="
                onShowRuns(item.runsUsingVersion, item.name, item.version + 1)
              "
              :class="item.runsUsingVersion?.length ? 'link' : ''"
            >
              {{ item.runsUsingVersion?.length }}
              <span v-if="item.runsUsingVersion?.length">
                ({{ $t("global.file.click-to-view") }})
              </span>
            </span>
          </template>

          <template #[`item.releasesUsingVersion`]="{ item }">
            <span
              @click="
                onShowReleases(
                  item.releasesUsingVersion,
                  item.name,
                  item.version + 1
                )
              "
              :class="item.releasesUsingVersion?.length ? 'link' : ''"
            >
              {{ item.releasesUsingVersion?.length }}
              <span v-if="item.releasesUsingVersion?.length">
                ({{ $t("global.file.click-to-view") }})
              </span>
            </span>
          </template>
        </ooliba-basic-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showRuns" persistent :width="1000">
      <v-card>
        <v-card-title>
          <span class="headline">
            {{ $t("global.file.runs-using-file") }} {{ showFileName }} (v.{{
              showFileVersion
            }})
          </span>
        </v-card-title>

        <v-card-text>
          <ooliba-basic-table
            v-model="selectedRuns"
            :headers="runTableHeaders"
            :items="runs"
            :loading="loadingRuns"
            sort-by="id"
            sort-desc
            must-sort
            @row-clicked="onRunClick"
          >
            <template #[`item.bpInstance`]="{ item }">
              <v-tooltip bottom v-if="item.bpInstance">
                <template #activator="{ on }">
                  <v-chip color="orange" dark small v-on="on">
                    {{ item.bpInstance.split(":")[0] }}
                  </v-chip>
                </template>
                <span>{{ item.bpInstance.split(":")[1] }}</span>
              </v-tooltip>
            </template>

            <template #[`item.dateCreated`]="{ item }">
              {{ formatDate(item.dateCreated) }}
            </template>
          </ooliba-basic-table>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn depressed class="button" @click="onCloseShowRuns">
            <span> {{ $t("global.action.close") }}</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showReleases" persistent :width="1000">
      <v-card>
        <v-card-title>
          <span class="headline">
            {{ $t("global.file.releases-using-file") }}
            {{ showFileName }} (v.{{ showFileVersion }})
          </span>
        </v-card-title>

        <v-card-text>
          <ooliba-basic-table
            v-model="selectedReleases"
            :headers="releaseTableHeaders"
            :items="releases"
            no-pagination
            sort-by="versionString"
            sort-desc
            :loading="loadingReleases"
            @row-clicked="onReleaseClick"
          >
            <template #[`item.dateCreated`]="{ item }">
              {{ formatDate(item.dateCreated) }}
            </template>
          </ooliba-basic-table>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn depressed class="button" @click="onCloseShowReleases">
            <span> {{ $t("global.action.close") }}</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDelete" persistent :width="700">
      <v-overlay absolute :value="deleting">
        <v-chip color="blue" dark>
          {{ $t("global.msg.deleting") }} ({{ deletingIndex }}/{{
            deletingVersions?.length
          }})
        </v-chip>
      </v-overlay>

      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("global.action.confirm") }}</span>
        </v-card-title>

        <v-card-text>
          {{
            $t("global.file.confirm-delete-files", [deletingVersions?.length])
          }}
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            depressed
            data-cy-id="confirm-delete-button"
            color="error"
            class="is-action"
            @click="confirmDelete"
          >
            {{ $t("global.action.yes-delete") }}
          </v-btn>

          <v-btn depressed class="button" @click="showDelete = false">
            <span> {{ $t("global.action.cancel") }}</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </module-template>
</template>

<script>
import ModuleTemplate from "@/components/layout/ModuleTemplate";
import OolibaBasicTable from "@/components/OolibaBasicTable";
import FileEnvChip from "@/components/FileEnvChip";
import BasicCardHorizontal from "@/components/BasicCardHorizontal";
import { get, remove } from "@/model/api";
import { formatNumber, formatDate, versionCompare } from "@/model/util";

export default {
  name: "FileCleanup",

  components: {
    ModuleTemplate,
    OolibaBasicTable,
    FileEnvChip,
    BasicCardHorizontal,
  },

  data() {
    return {
      unusedVersionsTableHeaders: [
        { text: "Id", value: "id" },
        {
          text: this.$t("global.concepts.name"),
          value: "name",
        },
        {
          text: this.$t("global.concepts.version"),
          value: "version",
        },
        {
          text: this.$t("global.concepts.size"),
          value: "sizeMB",
        },
      ],

      largestVersionsTableHeaders: [
        { text: "Id", value: "id" },
        {
          text: this.$t("global.concepts.name"),
          value: "name",
        },
        {
          text: this.$t("global.concepts.version"),
          value: "version",
        },
        {
          text: this.$t("global.file.num-runs-using-version"),
          value: "runsUsingVersion",
          sort: (a, b) => a.length - b.length,
        },
        {
          text: this.$t("global.file.release-using-version"),
          value: "releasesUsingVersion",
          sort: (a, b) => a.length - b.length,
        },
        {
          text: this.$t("global.concepts.size"),
          value: "sizeMB",
        },
      ],

      unusedFvHeaders: [
        {
          text: this.$t("global.file.num-file-versions"),
          value: "fileVersions",
        },
        {
          text: this.$t("global.file.num-unused-file-versions"),
          value: "unusedFileVersions",
        },
      ],

      diskSpaceHeaders: [
        {
          text: this.$t("global.file.available-disk-space", ["/ooliba"]),
          value: "availableDiskSpace",
        },
      ],

      runTableHeaders: [
        {
          text: "Id",
          value: "id",
          noHideable: true,
        },
        {
          text: this.$t("global.concepts.title"),
          value: "title",
          noHideable: true,
        },
        {
          text: this.$t("global.modules.module"),
          value: "module",
          noHideable: true,
          sortable: false,
        },
        {
          text: this.$t("global.modules.module-version"),
          value: "releaseVersion",
          sortable: false,
        },
        {
          text: this.$t("global.run.simulation"),
          value: "simulation",
          sortable: false,
        },
        {
          text: this.$t("global.concepts.created-on"),
          value: "dateCreated",
        },

        {
          text: this.$t("global.concepts.status"),
          value: "status",
          sortable: false,
        },

        {
          text: this.$t("global.business-process.bp-instance"),
          value: "bpInstance",
          align: "center",
          sortable: false,
        },
      ],

      releaseTableHeaders: [
        { text: this.$t("global.modules.module"), value: "moduleName" },
        {
          text: this.$t("global.concepts.version"),
          value: "versionString",
          sort: (a, b) => this.versionCompare(a, b),
        },
        { text: this.$t("global.concepts.uploaded-on"), value: "dateCreated" },
      ],

      panel: undefined,

      loadingUnused: false,
      loadingLargest: false,
      numFileVersions: undefined,
      numUnusedFileVersions: undefined,
      unusedPerEnv: undefined,
      largestFileVersions: undefined,
      diskSpaceInfo: undefined,

      selectedUnusedVersions: undefined,

      showRuns: false,
      showFileName: undefined,
      showFileVersion: undefined,
      runs: undefined,
      loadingRuns: false,
      selectedRuns: [],

      showReleases: false,
      releases: undefined,
      loadingReleases: false,
      selectedReleases: [],

      showDelete: false,
      deletingVersions: undefined,
      deletingIndex: 0,
      deleting: false,
    };
  },

  watch: {
    panel() {
      this.selectedUnusedVersions = undefined;
    },
  },

  computed: {
    unusedFvItem() {
      return {
        fileVersions: this.numFileVersions,
        unusedFileVersions: this.loadingUnused
          ? "Loading..."
          : this.numUnusedFileVersions,
      };
    },

    diskSpaceItem() {
      if (!this.diskSpaceInfo) {
        return;
      }
      const total = formatNumber(this.diskSpaceInfo.totalSpaceMB, 0);
      const available = formatNumber(this.diskSpaceInfo.usableSpaceMB, 0);
      const percentage = formatNumber(
        (this.diskSpaceInfo.usableSpaceMB / this.diskSpaceInfo.totalSpaceMB) *
          100,
        0
      );
      let percentageColor = "black";
      if (percentage <= 10) {
        percentageColor = "red";
      } else if (percentage <= 25) {
        percentageColor = "orange";
      }
      return {
        availableDiskSpace: available + " / " + total + " GB",
        percentage: percentage,
        percentageStyle: "color: " + percentageColor,
      };
    },
  },

  methods: {
    formatNumber,
    formatDate,
    versionCompare,

    onDeleteUnused(versions) {
      this.showDelete = true;
      this.deletingVersions = versions;
    },

    onRunClick(record) {
      if (record.id) {
        const runId = record.id;
        this.$router.push({ name: "Run", params: { runId: runId } });
      }
    },

    async confirmDelete() {
      this.deleting = true;
      for (let i = 0; i < this.deletingVersions.length; ++i) {
        this.deletingIndex = i + 1;
        const deletingId = this.deletingVersions[i].id;
        await remove("/file/file-version?versionId=" + deletingId).catch(
          (error) => {
            this.$store.commit("showError", error);
          }
        );
      }
      this.showDelete = false;
      this.deleting = false;
      this.deletingVersions = undefined;
      this.load();
    },

    onReleaseClick(record) {
      if (record.moduleName) {
        const moduleName = record.moduleName;
        this.$router.push({
          name: "ModuleVersions",
          params: { moduleName: moduleName },
        });
      }
    },

    async onShowRuns(runIds, fileName, fileVersion) {
      this.showRuns = true;
      this.loadingRuns = true;
      this.showFileName = fileName;
      this.showFileVersion = fileVersion;
      const idUrlParams = runIds.map((id) => "id=" + id).join("&");
      const runs = await get("simulationRun/by-id?" + idUrlParams).catch(
        (error) => {
          this.$store.commit("showError", error);
        }
      );
      this.setRunItems(runs);
      this.loadingRuns = false;
    },

    async onShowReleases(releaseIds, fileName, fileVersion) {
      this.showReleases = true;
      this.loadingReleases = true;
      this.showFileName = fileName;
      this.showFileVersion = fileVersion;
      const idUrlParams = releaseIds.map((id) => "id=" + id).join("&");
      this.releases = await get("release/by-id?" + idUrlParams).catch(
        (error) => {
          this.$store.commit("showError", error);
        }
      );
      this.loadingReleases = false;
    },

    onCloseShowRuns() {
      this.showRuns = false;
      this.runs = undefined;
      this.selectedRuns = [];
    },

    onCloseShowReleases() {
      this.showReleases = false;
      this.releases = undefined;
      this.selectedReleases = [];
    },

    setRunItems(runs) {
      this.runs = [];
      if (runs) {
        runs.forEach((value) => {
          const item = {};

          item.id = value.id;
          item.title = value.title;
          item.module = value.module;
          item.releaseVersion = `v${value.moduleVersion}`;
          item.simulation = value.simulation;
          item.dateCreated = value.dateCreated;
          item.status = value.translatedStatus;
          item.bpInstance = value.bpTitle
            ? "#" + value.bpId + ":" + value.bpTitle
            : "";

          let statusIcon = this.$store.getters.getStatusIcon(value.status);
          item.statusColor = statusIcon.statusColor;
          item.statusIcon = statusIcon.statusIcon;

          item.isSelectable = !value.running;

          this.runs.push(item);
        });
      }
    },

    async load() {
      this.loadingUnused = true;
      this.loadingLargest = true;
      this.panel = undefined;
      const versionsWithSize = await get(
        "/file/count-file-versions-with-size"
      ).catch((error) => {
        this.$store.commit("showError", error);
      });
      this.numFileVersions =
        versionsWithSize?.count +
        " (" +
        formatNumber(versionsWithSize?.totalSizeMB, 0) +
        " MB)";

      this.diskSpaceInfo = await get("/file/disk-space-info").catch((error) => {
        this.$store.commit("showError", error);
      });

      this.largestFileVersions = await get("/file/largest-file-versions").catch(
        (error) => {
          this.$store.commit("showError", error);
        }
      );
      this.loadingLargest = false;

      const unusedFileVersions = await get("/file/unused-file-versions").catch(
        (error) => {
          this.$store.commit("showError", error);
        }
      );
      this.numUnusedFileVersions =
        unusedFileVersions?.count +
        " (" +
        formatNumber(unusedFileVersions?.totalSizeMB, 0) +
        " MB)";
      this.unusedPerEnv = unusedFileVersions?.fileVersionsPerEnv;
      this.loadingUnused = false;
    },
  },

  async created() {
    this.load();
  },
};
</script>
